/**
 * AktiebokPage - Digital aktiebok pillar page
 *
 * SEO Target: /aktiebok
 * Primary keywords: digital aktiebok, aktiebok online, aktiebok aktiebolag
 */

import { useState } from "react";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function AktiebokPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const breadcrumbItems = [{ label: "Digital aktiebok" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Digital aktiebok", url: "/aktiebok" },
  ]);

  return (
    <>
      <PageMeta
        title="Digital aktiebok online | Ägarförteckning enligt ABL | Kvanten"
        description="Digital aktiebok för aktiebolag enligt ABL 5 kap. Automatisk uppdatering, ägarförteckning, aktiehistorik och export. Ingår i Kvanten-plattformen."
        keywords="digital aktiebok, aktiebok online, aktiebok aktiebolag, ägarförteckning, aktiebok ABL, aktieägarregister"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/aktiebok`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-4">
                Ingår i Kvanten
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Digital aktiebok
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Alla aktiebolag måste föra aktiebok enligt aktiebolagslagen. Med Kvantens
                digitala aktiebok hanterar du ägare, aktier och transaktioner enkelt online.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Skapa digital aktiebok
                  <ArrowRightIcon />
                </button>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckIcon />
                  <span>Ingår i årsredovisningspriset</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckIcon />
                  <span>Uppfyller ABL 5 kap</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Vad ingår?</h2>
              <ul className="space-y-4">
                {[
                  "Komplett ägarförteckning",
                  "Registrering av aktietransaktioner",
                  "Historik över alla ägarbyten",
                  "PDF-export av aktieboken",
                  "Automatisk koppling till årsredovisningen",
                  "Stöd för flera aktieklasser",
                  "Obegränsat antal ägare",
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What is aktiebok */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Vad är en aktiebok?</h2>
              <p className="text-gray-600 mb-6">
                Aktieboken är ett register över alla aktier i ett aktiebolag och vem som äger
                dem. Enligt <strong>Aktiebolagslagen (ABL) 5 kap</strong> är alla aktiebolag
                skyldiga att föra aktiebok.
              </p>
              <p className="text-gray-600 mb-6">Aktieboken ska innehålla:</p>
              <ul className="space-y-3">
                {[
                  "Aktieägarnas namn och personnummer/organisationsnummer",
                  "Postadress för varje aktieägare",
                  "Antal aktier per ägare",
                  "Aktiernas nummer (eller nummerserie)",
                  "Aktieklasser (om flera finns)",
                  "Datum för förvärv",
                  "Eventuella förbehåll (hembuds-, förköps-, samtyckesförbehåll)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Varför digital aktiebok?
              </h3>
              <div className="space-y-4">
                {[
                  {
                    title: "Alltid uppdaterad",
                    description:
                      "Slipp pappersaktiebok som glöms bort. Digital aktiebok uppdateras enkelt vid varje transaktion.",
                  },
                  {
                    title: "Säker lagring",
                    description:
                      "Aktieboken lagras säkert i molnet med automatisk backup. Ingen risk att förlora den.",
                  },
                  {
                    title: "Enkel export",
                    description:
                      "Exportera aktieboken som PDF när du behöver visa den för bank, köpare eller myndighet.",
                  },
                  {
                    title: "Automatisk koppling",
                    description:
                      "Ägarinformation kopplas automatiskt till förvaltningsberättelsen i årsredovisningen.",
                  },
                ].map((item, i) => (
                  <div key={i}>
                    <h4 className="font-semibold text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Funktioner</h2>
            <p className="text-xl text-gray-600">Allt du behöver för att hantera bolagets ägande</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Lägg till aktieägare",
                description:
                  "Registrera nya aktieägare med namn, personnummer/orgnr, adress och aktieinnehav.",
              },
              {
                title: "Registrera transaktioner",
                description:
                  "Dokumentera köp, försäljning, arv, gåvor och andra ägarbyten med datum och pris.",
              },
              {
                title: "Flera aktieklasser",
                description:
                  "Stöd för A-aktier, B-aktier och andra aktieklasser med olika röstvärden.",
              },
              {
                title: "Förbehåll",
                description:
                  "Registrera hembudsförbehåll, förköpsförbehåll och samtyckesförbehåll enligt bolagsordningen.",
              },
              {
                title: "Ägarhistorik",
                description:
                  "Full historik över alla ägarförändringar sedan bolagets bildande.",
              },
              {
                title: "PDF-export",
                description:
                  "Exportera aktieboken som PDF för att visa för bank, köpare eller myndigheter.",
              },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal requirements */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Lagkrav på aktiebok</h2>
          <div className="prose prose-gray max-w-none">
            <p>
              Enligt <strong>Aktiebolagslagen (ABL) 5 kap 1-8 §§</strong> gäller följande:
            </p>
            <ul>
              <li>
                <strong>Skyldighet att föra aktiebok:</strong> Styrelsen ansvarar för att
                aktieboken upprättas och hålls tillgänglig.
              </li>
              <li>
                <strong>Offentlighet:</strong> Aktieboken är offentlig. Var och en har rätt att
                ta del av den hos bolaget.
              </li>
              <li>
                <strong>Uppdatering:</strong> Ändringar ska införas utan dröjsmål. Vid överlåtelse
                ska införing ske inom 14 dagar från det att bolaget underrättats.
              </li>
              <li>
                <strong>Förvaring:</strong> Aktieboken ska förvaras så länge bolaget består och
                minst 10 år efter likvidation.
              </li>
              <li>
                <strong>Sanktioner:</strong> Styrelseledamot som uppsåtligen eller av oaktsamhet
                underlåter att föra aktiebok kan dömas till böter.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Kom igång med digital aktiebok
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Aktieboken ingår utan extra kostnad när du skapar årsredovisning med Kvanten.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center px-8 py-4 text-lg font-medium text-purple-600 bg-white rounded-lg hover:bg-purple-50 transition-colors"
          >
            Skapa konto gratis
            <ArrowRightIcon />
          </button>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
